import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Download, Search } from "lucide-react";
import { reApi, errMsg, fmtDateTime, pretty } from "@/lib/residentApi";
import { Switch } from "@/components/ui/switch";
import { inputCls, ErrorLine } from "@/components/nds/windows/resident/ui";
import { ContactTable, MissingNote } from "./ContactTable";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/re/contacts`;
const selCls = "px-2 py-1.5 bg-black/40 border border-white/10 text-nex-silver text-xs rounded-sm outline-none focus:border-nex-gold/60";

async function blobError(e) {
  try { return JSON.parse(await e.response.data.text()).detail || errMsg(e); } catch { return errMsg(e); }
}

function Recent({ list }) {
  return (
    <div data-testid="contacts-recent" className="border-t border-white/10 pt-3">
      <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-nex-muted mb-2">Recent downloads</div>
      {!list.length ? <div className="text-xs text-nex-muted" data-testid="contacts-recent-empty">No downloads yet.</div> : (
        <ul className="space-y-1">{list.map((d) => (
          <li key={d.id} data-testid={`contacts-recent-${d.id}`} className="flex flex-wrap gap-x-3 text-xs text-nex-silver">
            <span>{d.actor?.name || d.actor?.email}</span><span className="text-nex-muted">{fmtDateTime(d.at)}</span>
            <span className="font-mono text-nex-gold">{d.count} resident{d.count === 1 ? "" : "s"} · {(d.format || "").toUpperCase()}</span>
          </li>))}</ul>)}
    </div>
  );
}

export function ResidentContactList() {
  const [archived, setArchived] = useState(false);
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState([]);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("active");
  const [prop, setProp] = useState("all");
  const [selected, setSelected] = useState(() => new Set());
  const loadRecent = () => reApi.get("/contacts/recent").then((d) => setRecent(d.downloads)).catch(() => {});
  useEffect(() => { setData(null); reApi.get("/contacts/residents", { include_archived: archived }).then((d) => { setData(d); const ok = new Set(d.residents.map((r) => r.id)); setSelected((s) => new Set([...s].filter((id) => ok.has(id)))); }).catch((e) => setError(errMsg(e))); }, [archived]);
  useEffect(() => { loadRecent(); }, []);
  const all = data?.residents || [];
  const statuses = useMemo(() => [...new Set(all.map((r) => r.status))].sort(), [all]);
  const props = useMemo(() => [...new Set(all.map((r) => r.property))].sort((a, b) => (a === "Unassigned") - (b === "Unassigned") || a.localeCompare(b)), [all]);
  const rows = all.filter((r) => (status === "all" || r.status === status) && (prop === "all" || r.property === prop) && (!q || r.name.toLowerCase().includes(q.toLowerCase())));
  const hidden = [...selected].filter((id) => !rows.some((r) => r.id === id)).length;
  useEffect(() => { setInfo(null); }, [q, status, prop, archived]);
  const allOn = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => { const n = new Set(s); rows.forEach((r) => (allOn ? n.delete(r.id) : n.add(r.id))); return n; });
  const cap = data?.cap || 500;
  const over = selected.size > cap;
  const download = async (format) => {
    setBusy(format); setError(null); setInfo(null);
    try {
      const res = await axios.post(`${BASE}/export`, { resident_ids: [...selected], format, include_archived: archived, filters: { status, property: prop, search: q } },
        { headers: { Authorization: `Bearer ${localStorage.getItem("nex_token")}` }, responseType: "blob" });
      const name = (res.headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || `resident-contacts.${format}`;
      const url = URL.createObjectURL(res.data);
      Object.assign(document.createElement("a"), { href: url, download: name }).click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      const skipped = Number(res.headers["x-skipped-count"] || 0);
      setInfo(`${res.headers["x-row-count"] || selected.size} resident(s) downloaded as ${format.toUpperCase()}.${skipped ? ` ${skipped} resident${skipped === 1 ? "" : "s"} could not be read${archived ? "" : " (or archived since selected)"}.` : ""}`);
      loadRecent();
    } catch (e) { setError(await blobError(e)); }
    setBusy(null);
  };
  return (
    <div data-testid="resident-contact-list" className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px]"><Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-nex-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search residents" data-testid="contacts-search" className={`${inputCls} pl-8 py-1.5`} /></div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="contacts-filter-status" className={selCls} aria-label="Status">
          <option value="all">All statuses</option>{statuses.map((s) => <option key={s} value={s}>{pretty(s)}</option>)}</select>
        <select value={prop} onChange={(e) => setProp(e.target.value)} data-testid="contacts-filter-property" className={selCls} aria-label="Property">
          <option value="all">All properties</option>{props.map((p) => <option key={p} value={p}>{p}</option>)}</select>
        <label className="inline-flex items-center gap-2 text-xs text-nex-muted cursor-pointer">
          <Switch checked={archived} onCheckedChange={setArchived} data-testid="contacts-include-archived" />Include archived/exited</label>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <span data-testid="contacts-selected-count" className="font-mono text-xs text-nex-gold">{selected.size} selected</span>
        <span data-testid="contacts-shown-count" className="text-xs text-nex-muted">{data ? `${rows.length} shown` : "Loading…"}</span>
        {hidden > 0 && <span data-testid="contacts-hidden-count" className="text-xs text-amber-300">{hidden} selected hidden by filters</span>}
        {selected.size > 0 && <button type="button" onClick={() => setSelected(new Set())} data-testid="contacts-clear" className="text-[11px] text-nex-muted underline hover:text-nex-silver">Clear</button>}
        <div className="ml-auto flex gap-2">{["csv", "pdf"].map((f) => (
          <button key={f} type="button" disabled={!selected.size || over || !!busy} onClick={() => download(f)} data-testid={`contacts-download-${f}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-nex-gold/50 text-nex-gold rounded-sm font-mono text-[10px] tracking-[0.16em] uppercase hover:bg-nex-gold/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            <Download className="w-3.5 h-3.5" />{busy === f ? "Building…" : `Download ${f.toUpperCase()}`}</button>))}</div>
      </div>
      {over && <div data-testid="contacts-cap-warning" className="text-xs text-red-300">One download is capped at {cap} residents. You selected {selected.size}. Narrow the selection.</div>}
      <ErrorLine testId="contacts-error">{error}</ErrorLine>
      {info && <div data-testid="contacts-info" className="text-xs text-emerald-300">{info}</div>}
      {data?.unreadable > 0 && <div data-testid="contacts-unreadable" className="text-xs text-amber-300">{data.unreadable} resident{data.unreadable === 1 ? "" : "s"} could not be read</div>}
      <MissingNote rows={rows} />
      <ContactTable rows={rows} selected={selected} toggle={toggle} allOn={allOn} toggleAll={toggleAll} />
      <Recent list={recent} />
    </div>
  );
}
